import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Mail, CheckCircle, Clock, FileText, User, Building, IndianRupee, Printer, Package, Truck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useBusiness } from '../../contexts/BusinessContext';
import { purchaseReceivesAPI } from '../../services/part2-api';
import { getApiErrorMessage } from '../../utils/apiErrors';
import ConfirmModal from '../../components/modals/ConfirmModal';

const PurchaseReceiveDetailPage = () => {
  const { businessId, receiveId } = useParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const [receive, setReceive] = useState(null);
  const [loading, setLoading] = useState(true);

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid && receiveId) {
      fetchReceive();
    }
  }, [bid, receiveId]);

  const [confirmModal, setConfirmModal] = useState(null);

  const fetchReceive = async () => {
    try {
      setLoading(true);
      const response = await purchaseReceivesAPI.get(receiveId);
      setReceive(response.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load receive'));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    setConfirmModal({
      title: 'Confirm Purchase Receive',
      description: `Confirm ${receive?.receive_number}? This will update inventory stock levels.`,
      confirmLabel: 'Confirm & Update Inventory',
      confirmClass: 'bg-blue-600 hover:bg-blue-700',
      icon: CheckCircle,
      iconBgClass: 'bg-blue-100',
      iconClass: 'text-blue-600',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await purchaseReceivesAPI.confirm(receiveId);
          toast.success('Purchase receive confirmed');
          fetchReceive();
        } catch (error) {
          toast.error(getApiErrorMessage(error, 'Failed to confirm'));
        }
      },
    });
  };

  const handleDownloadPdf = async () => {
    try {
      const response = await purchaseReceivesAPI.pdf(receiveId);
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `GRN_${receive?.receive_number}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'PDF download failed'));
    }
  };

  const handleBack = () => {
    navigate(`/business/${bid}/purchase-receives`);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <div className="w-10 h-10 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[14px] text-slate-500">Loading receive details...</p>
        </div>
      </div>
    );
  }

  if (!receive) {
    return (
      <div className="h-full flex items-center justify-center bg-[#F8F9FB]">
        <div className="text-center">
          <Package className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-[16px] text-slate-600 font-medium">Purchase Receive not found</p>
          <button onClick={handleBack} className="mt-4 text-teal-600 hover:underline text-[14px]">
            ← Back to Purchase Receives
          </button>
        </div>
      </div>
    );
  }

  const statusConfig = {
    DRAFT: { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, label: 'Draft' },
    CONFIRMED: { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: CheckCircle, label: 'Confirmed' },
  };

  const statusBadge = statusConfig[receive.status] || statusConfig.DRAFT;
  const StatusIcon = statusBadge.icon;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const items = receive.items || [];

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={handleBack}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-[20px] font-bold text-slate-800">{receive.receive_number}</h1>
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[12px] font-semibold ${statusBadge.bg} ${statusBadge.text}`}>
                    <StatusIcon className="w-3.5 h-3.5" />
                    {statusBadge.label}
                  </span>
                </div>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  Received on {formatDate(receive.receive_date)} • Against PO: <span className="text-teal-600 font-medium">{receive.po_number}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {receive.status === 'DRAFT' && (
                <button
                  onClick={handleConfirm}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
                >
                  <CheckCircle className="w-4 h-4" />
                  Confirm & Update Inventory
                </button>
              )}
              {receive.status === 'CONFIRMED' && (
                <button
                  onClick={() => navigate(`/business/${bid}/purchase-invoices/new?source_type=RECEIVE&source_id=${receiveId}&supplier_id=${receive.supplier_id || receive.party_id}`)}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-blue-600/20 active:scale-[0.98]"
                >
                  <FileText className="w-4 h-4" />
                  Convert → Purchase Invoice
                </button>
              )}
              <div className="w-px h-8 bg-slate-200 mx-1"></div>
              <button onClick={handleDownloadPdf} className="inline-flex items-center justify-center w-10 h-10 text-slate-600 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 active:scale-[0.98]" title="Download PDF">
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Supplier Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Building className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Supplier</h2>
                </div>
                
                {(receive.supplier_name || receive.party_name) ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-[16px] font-bold text-teal-700">
                          {(receive.supplier_name || receive.party_name || '').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <p className="text-[14px] font-semibold text-slate-800 truncate">{receive.supplier_name || receive.party_name}</p>
                        <p className="text-[12px] text-slate-500">Supplier</p>
                      </div>
                    </div>

                    {(receive.supplier_phone || receive.party_phone) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <User className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{receive.supplier_phone || receive.party_phone}</span>
                      </div>
                    )}

                    {(receive.supplier_email || receive.party_email) && (
                      <div className="flex items-center gap-2 text-[13px] text-slate-600">
                        <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{receive.supplier_email || receive.party_email}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-[13px] text-slate-500">No supplier info</p>
                  </div>
                )}
              </div>

              {/* Receive Info Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Receive Info</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Receive Date</span>
                    <span className="text-[13px] font-medium text-slate-700">{formatDate(receive.receive_date)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[12px] text-slate-500">Purchase Order</span>
                    <button 
                      onClick={() => navigate(`/business/${bid}/purchase-orders/${receive.purchase_order_id || receive.order_id}`)}
                      className="text-[13px] font-medium text-teal-600 hover:underline"
                    >
                      {receive.po_number}
                    </button>
                  </div>
                  {receive.reference_number && (
                    <div className="flex justify-between items-center">
                      <span className="text-[12px] text-slate-500">Reference</span>
                      <span className="text-[13px] font-medium text-slate-700">{receive.reference_number}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Inventory Impact Card */}
              {receive.status === 'CONFIRMED' && (
                <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <Truck className="w-4 h-4 text-white" />
                    </div>
                    <h2 className="text-[14px] font-semibold text-emerald-800">Inventory Updated</h2>
                  </div>
                  <p className="text-[12px] text-emerald-700 leading-relaxed">
                    Stock levels have been updated for {items.length} item(s) based on received quantities.
                  </p>
                </div>
              )}
            </div>

            {/* CENTER COLUMN - Items Table (6 cols) */}
            <div className="col-span-12 lg:col-span-6">
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden hover:shadow-md transition-shadow duration-200">
                <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Received Items</h2>
                      <p className="text-[12px] text-slate-500">{items.length} item{items.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                </div>
                
                {items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">#</th>
                          <th className="px-5 py-3 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Item Details</th>
                          <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Ordered</th>
                          <th className="px-5 py-3 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Received</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Rate</th>
                          <th className="px-5 py-3 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/50 transition-colors duration-150">
                            <td className="px-5 py-4 text-[13px] text-slate-400 font-medium">{index + 1}</td>
                            <td className="px-5 py-4">
                              <p className="text-[14px] font-medium text-slate-800">{item.item_name}</p>
                              {item.hsn_code && (
                                <p className="text-[11px] text-slate-400 mt-0.5">HSN: {item.hsn_code}</p>
                              )}
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-lg text-[13px] font-medium text-slate-600">
                                {item.ordered_quantity} {item.unit || 'Nos'}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-emerald-100 rounded-lg text-[13px] font-bold text-emerald-700">
                                {item.received_quantity} {item.unit || 'Nos'}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right text-[13px] text-slate-600">
                              ₹{(item.rate || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-5 py-4 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-[14px] text-slate-500">No items in this receive</p>
                  </div>
                )}

                {receive.notes && (
                  <div className="px-5 py-4 bg-amber-50 border-t border-amber-100">
                    <p className="text-[11px] text-amber-600 uppercase tracking-wide font-semibold mb-1">Notes</p>
                    <p className="text-[13px] text-amber-800">{receive.notes}</p>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN - Summary (3 cols) */}
            <div className="col-span-12 lg:col-span-3 space-y-4">
              
              {/* Summary Card */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-5 shadow-sm border border-emerald-200/60">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <IndianRupee className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Receive Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">{'\u20B9'}{(receive.total_value || receive.grand_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-emerald-600 font-medium">+ ₹{(receive.tax_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="border-t border-emerald-200 pt-4 mt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Total Value</span>
                      <span className="text-[22px] font-bold text-emerald-600">
                        {'\u20B9'}{(receive.grand_total || receive.total_value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Card */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-sm">
                    <Clock className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Status</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${receive.status === 'DRAFT' ? 'bg-amber-500' : 'bg-emerald-500'}`}></div>
                    <div>
                      <p className="text-[13px] font-medium text-slate-700">Created</p>
                      <p className="text-[11px] text-slate-500">{formatDate(receive.created_at)}</p>
                    </div>
                  </div>
                  
                  {receive.status === 'CONFIRMED' && (
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                      <div>
                        <p className="text-[13px] font-medium text-slate-700">Confirmed</p>
                        <p className="text-[11px] text-slate-500">Inventory updated</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-100">
                <h2 className="text-[14px] font-semibold text-slate-800 mb-4">Quick Actions</h2>
                <div className="space-y-2">
                  {receive.status === 'DRAFT' && (
                    <button
                      onClick={handleConfirm}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-teal-50 hover:bg-teal-100 transition-colors duration-200"
                    >
                      <CheckCircle className="w-4 h-4 text-teal-600" />
                      <span className="text-[13px] font-medium text-teal-700">Confirm & Update Inventory</span>
                    </button>
                  )}
                  {receive.status === 'CONFIRMED' && (
                    <button
                      onClick={() => navigate(`/business/${bid}/purchase-invoices/new?source_type=RECEIVE&source_id=${receiveId}&supplier_id=${receive.supplier_id || receive.party_id}`)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-blue-50 hover:bg-blue-100 transition-colors duration-200"
                    >
                      <FileText className="w-4 h-4 text-blue-600" />
                      <span className="text-[13px] font-medium text-blue-700">Convert → Purchase Invoice</span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate(`/business/${bid}/purchase-orders/${receive.purchase_order_id || receive.order_id}`)}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left rounded-xl bg-slate-50 hover:bg-slate-100 transition-colors duration-200"
                  >
                    <Package className="w-4 h-4 text-slate-600" />
                    <span className="text-[13px] font-medium text-slate-700">View Purchase Order</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={!!confirmModal}
        title={confirmModal?.title}
        description={confirmModal?.description}
        confirmLabel={confirmModal?.confirmLabel}
        confirmClass={confirmModal?.confirmClass}
        icon={confirmModal?.icon}
        iconBgClass={confirmModal?.iconBgClass}
        iconClass={confirmModal?.iconClass}
        onConfirm={confirmModal?.onConfirm}
        onClose={() => setConfirmModal(null)}
      />
    </div>
  );
};

export default PurchaseReceiveDetailPage;
